import { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  MessageCircle,
  Inbox,
  Settings,
  Users,
  Coins,
  Bell,
  Phone,
  PhoneOff,
  Loader2,
  PhoneCall,
  GitBranch,
  Bot,
  History,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { useLicenseStore } from '@/stores/license-store';
import { useNotificationStore } from '@/stores/notification-store';
import { useCallStore, selectHasDismissedActiveCalls } from '@/stores/call-store';
import { isLiveCallStatus } from '@/types/voice';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { Button } from '@/components/ui/button';
import { NotificationPanel } from '@/components/common/notification-panel';
import { TrialBadge } from '@/components/trial/trial-badge';
import { formatCredits } from '@/lib/utils/format';
import type { ViewType } from '@/config/constants';
import { INBOX_SUPPORTED_TPAS, CHAT_ONLY_TPAS, CALL_APPS } from '@/config/constants';

type NavItem = { view: ViewType; label: string; icon: typeof MessageSquare };

const DEFAULT_NAV_ITEMS: NavItem[] = [
  { view: 'MESSAGE_FORM', label: 'Compose', icon: MessageSquare },
  { view: 'CONVERSATIONS', label: 'Inbox', icon: Inbox },
];

const CRM_NAV_ITEMS: NavItem[] = [
  { view: 'MESSAGE_FORM', label: 'Compose', icon: MessageSquare },
  { view: 'CHAT', label: 'Chat', icon: MessageCircle },
  { view: 'CONVERSATIONS', label: 'Inbox', icon: Inbox },
];

const CALL_NAV_ITEMS: NavItem[] = [
  { view: 'CONVERSATIONS', label: 'Call History', icon: PhoneCall },
  { view: 'PHONE_TREE', label: 'Phone Tree', icon: GitBranch },
];

export function InboxNav() {
  const activeView = useUIStore((s) => s.activeView);
  const setView = useUIStore((s) => s.setView);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const credits = useLicenseStore((s) => s.credits);
  const alertCount = useNotificationStore((s) => s.alertCount);
  const togglePanel = useNotificationStore((s) => s.togglePanel);

  const extensionNameNav = useAppStore((s) => s.extensionName) ?? '';
  const appCodeTPA = useAppStore((s) => s.appCodeTPA) ?? '';
  const inboxSupported = INBOX_SUPPORTED_TPAS.has(appCodeTPA);

  const isCallApp = (CALL_APPS as readonly string[]).includes(appCodeTPA);
  const isTwilio = appCodeTPA === 'twilio';

  const isAdmin = !selectedAdminUserId;
  const callNavItems = isAdmin ? CALL_NAV_ITEMS : CALL_NAV_ITEMS.filter((i) => i.view !== 'PHONE_TREE');

  const baseItems = isCallApp ? callNavItems : crmRecordContext ? CRM_NAV_ITEMS : DEFAULT_NAV_ITEMS;
  const navItems = [
    ...baseItems,
    // TODO: Re-enable after launch
    // ...(isTwilio && isAdmin ? [{ view: 'CHAT_AUTOMATION' as const, label: 'Automation', icon: Bot }] : []),
  ].filter((item) => {
    if (!inboxSupported && (item.view === 'CONVERSATIONS' || item.view === 'CHAT')) return false;
    // Hide all messaging nav items when Compose view is active in CRM multi-record context
    if (activeView === 'MESSAGE_FORM' && crmRecordContext && (item.view === 'CONVERSATIONS' || item.view === 'CHAT' || item.view === 'MESSAGE_FORM')) return false;
    // Hide Compose nav in single CRM record context (phone fields resolved = single record)
    if (crmRecordContext && crmPhoneFields.length > 0 && item.view === 'MESSAGE_FORM') return false;
    if (CHAT_ONLY_TPAS.has(appCodeTPA) && item.view === 'CONVERSATIONS') return false;
    return true;
  });

  return (
    <aside className="flex w-[52px] flex-col items-center border-r border-slate-600 dark:border-slate-800 bg-slate-700 dark:bg-slate-900 py-2 shrink-0">
      {/* Logo */}
      <div className="flex flex-col items-center mb-1">
        <img
          src={appCodeTPA === 'pinnacle' ? '/images/logos/pinnacle.png' : '/images/ulgebra-icon.png'}
          alt={appCodeTPA === 'pinnacle' ? 'Pinnacle' : 'Ulgebra'}
          className="h-8 w-8 rounded-lg object-cover"
        />
        <span className="mt-1">
          <TrialBadge compact />
        </span>
      </div>

      <div className="w-6 border-t border-slate-500/40 dark:border-slate-700 my-1.5" />

      {/* Nav items */}
      <nav className="flex flex-col items-center gap-0.5">
        {navItems.map(({ view, label, icon: Icon }) => {
          const isActive = activeView === view;
          return (
            <button
              key={view}
              data-testid={`nav-${label.toLowerCase()}`}
              onClick={() => {
                // Close CRM call manager when navigating to a different view
                if (useCallStore.getState().crmCallManagerOpen) {
                  useCallStore.getState().toggleCrmCallManager();
                }
                setView(view);
              }}
              className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-slate-300 hover:bg-white/10 hover:text-white'
              }`}
              title={label}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </nav>

      {/* Phone / Call Manager */}
      <PhoneButton />

      {/* Calls Inbox — opens CRM Call Manager with history */}
      <CallsInboxButton />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom actions */}
      <div className="flex flex-col items-center gap-0.5">
        {/* Notifications — the panel behind this bell lists inbox items, so it
            goes wherever the inbox goes. The header carries the same bell and
            the same gate; this rail is the second one. */}
        {!CHAT_ONLY_TPAS.has(appCodeTPA) && (
          <div className="relative" data-notification-bell>
            <button
              onClick={togglePanel}
              className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
              title="Notifications"
            >
              <Bell className="h-4 w-4" />
              {alertCount > 0 && (
                <span className="absolute top-0.5 right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
                  {alertCount > 9 ? '9+' : alertCount}
                </span>
              )}
            </button>
            <NotificationPanel position="right" />
          </div>
        )}

        {/* Settings */}
        <button
          data-testid="nav-settings"
          onClick={() => setView('SETTINGS')}
          className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          title="Settings"
        >
          <Settings className="h-4 w-4" />
        </button>

        {/* Profile */}
        <button
          onClick={() => setView('SETTINGS')}
          className="relative mt-1 mb-1"
          title="Profile & Settings"
        >
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName}
              className="h-7 w-7 rounded-full object-cover ring-1 ring-slate-500"
            />
          ) : (
            <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center text-xs font-medium text-white">
              {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
          {selectedAdminUserId && (
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-amber-500 ring-2 ring-slate-700">
              <Users className="h-1.5 w-1.5 text-white" />
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}

/** Phone icon with softphone status dot — opens Call Manager */
function PhoneButton() {
  const [showEnablePrompt, setShowEnablePrompt] = useState(false);
  const promptRef = useRef<HTMLDivElement>(null);

  const extensionName = useAppStore((s) => s.extensionName);
  const enableVoiceCalling = useSettingsStore((s) => s.enableVoiceCalling);
  const updateEnableVoiceCalling = useSettingsStore((s) => s.updateEnableVoiceCalling);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const softphoneState = useCallStore((s) => s.softphoneState);
  const voiceInitWaiting = useCallStore((s) => s.voiceInitWaiting);
  const isAvailable = useCallStore((s) => s.isAvailable);
  const toggleCallManager = useCallStore((s) => s.toggleCallManager);
  const callManagerOpen = useCallStore((s) => s.callManagerOpen);
  const activeCalls = useCallStore((s) => s.activeCalls);
  const hasDismissedActiveCalls = useCallStore(selectHasDismissedActiveCalls);
  const undismissAllCalls = useCallStore((s) => s.undismissAllCalls);

  const isOrgAdmin = !selectedAdminUserId;

  const hasActiveCalls = activeCalls.some((c) => isLiveCallStatus(c.status));

  // Close prompt on outside click
  useEffect(() => {
    if (!showEnablePrompt) return;
    const handler = (e: MouseEvent) => {
      if (promptRef.current && !promptRef.current.contains(e.target as Node)) {
        setShowEnablePrompt(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEnablePrompt]);

  // Only show for Twilio extensions
  const isTwilioExt = extensionName?.startsWith('twilio') ?? false;
  if (!isTwilioExt) return null;

  const handleClick = () => {
    if (!enableVoiceCalling) {
      setShowEnablePrompt((prev) => !prev);
      return;
    }
    if (hasDismissedActiveCalls) {
      undismissAllCalls();
      return;
    }
    toggleCallManager();
  };

  const handleEnable = () => {
    updateEnableVoiceCalling(true);
    setShowEnablePrompt(false);
  };

  return (
    <div className="relative mt-1.5" ref={promptRef}>
      <button
        onClick={handleClick}
        data-call-manager-trigger
        className={cn(
          'relative h-9 w-9 rounded-lg flex items-center justify-center transition-colors',
          !enableVoiceCalling
            ? 'text-slate-500 hover:bg-white/10 hover:text-slate-300'
            : callManagerOpen
              ? 'bg-white/15 text-white'
              : hasActiveCalls
                ? 'text-emerald-400 hover:bg-white/10'
                : 'text-slate-300 hover:bg-white/10 hover:text-white'
        )}
        title={!enableVoiceCalling ? 'Voice Calling (Off)' : hasDismissedActiveCalls ? 'Reopen active call' : voiceInitWaiting ? 'Initializing voice...' : 'Call Manager'}
      >
        {!enableVoiceCalling ? (
          <PhoneOff className="h-4 w-4" />
        ) : voiceInitWaiting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Phone className="h-4 w-4" />
        )}
        {/* Active call indicator — pulsing green when call widget is dismissed */}
        {hasDismissedActiveCalls ? (
          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-400 animate-pulse ring-1 ring-slate-700" />
        ) : enableVoiceCalling && !voiceInitWaiting && (
          <span
            className={cn(
              'absolute top-1 right-1 h-2 w-2 rounded-full ring-1 ring-slate-700',
              softphoneState === 'registered' && isAvailable
                ? 'bg-emerald-400'
                : softphoneState === 'registered' && !isAvailable
                  ? 'bg-amber-400'
                  : softphoneState === 'registering'
                    ? 'bg-blue-400 animate-pulse'
                    : softphoneState === 'unregistered'
                      ? 'bg-slate-500'
                      : 'bg-destructive'
            )}
          />
        )}
      </button>

      {/* Enable voice calling prompt — positioned to the right of the sidebar */}
      {showEnablePrompt && (
        <div className="absolute left-full top-0 ml-2 z-50 w-60 rounded-xl border border-border bg-popover p-4 shadow-xl animate-in fade-in-0 zoom-in-95">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <PhoneCall className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-semibold text-foreground">Voice Calling</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            {isOrgAdmin
              ? 'Voice calling lets you make and receive calls directly from the app.'
              : 'Voice calling is not enabled. Ask your account admin to turn it on.'}
          </p>
          {isOrgAdmin && (
            <Button size="sm" onClick={handleEnable} className="w-full h-8 text-xs gap-1.5">
              <Phone className="h-3.5 w-3.5" />
              Enable Voice Calling
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Calls Inbox button — toggles CRM context call manager panel */
function CallsInboxButton() {
  const extensionName = useAppStore((s) => s.extensionName);
  const crmCallManagerOpen = useCallStore((s) => s.crmCallManagerOpen);
  const toggleCrmCallManager = useCallStore((s) => s.toggleCrmCallManager);

  // Only show for Twilio extensions
  const isTwilioExt = extensionName?.startsWith('twilio') ?? false;
  if (!isTwilioExt) return null;

  return (
    <button
      onClick={toggleCrmCallManager}
      className={cn(
        'h-9 w-9 rounded-lg flex items-center justify-center transition-colors mt-0.5',
        crmCallManagerOpen
          ? 'bg-white/15 text-white'
          : 'text-slate-300 hover:bg-white/10 hover:text-white'
      )}
      title="Calls"
    >
      <History className="h-4 w-4" />
    </button>
  );
}
